// An estimator's Pricing tab: "Detailed quotes" (docs/10 §11). With QuoteFlow,
// every estimator (Compact, Full width, hosted) offers "Get my detailed quote"
// after the estimate, unless switched off here: the quote is built from the
// company's catalogue and packages and sent to the homeowner at once. Compact
// offers it for homes with an EPC (the quote's answers come from the certificate).
// Needs the QuoteFlow plan and the quote terms; the estimate itself always
// uses this tab's size prices.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useApi } from "../../api";
import { Toggle } from "../../components/controls";
import { useQuotesApi, type QuoteAccess } from "../../quotes/api";
import { useWidget } from "./WidgetLayout";

export function InstantQuotesCard() {
  const { widget, setWidget } = useWidget();
  const api = useApi();
  const quotesApi = useQuotesApi();
  const [access, setAccess] = useState<QuoteAccess | null>(null);
  const [packages, setPackages] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const style = widget.branding?.layout.style ?? "small";
  const detailed = style !== "contact";
  // On by default: only an explicit "off" turns it off
  const on = widget.quotes?.enabled !== false;

  useEffect(() => {
    quotesApi
      .getAccess()
      .then((a) => {
        setAccess(a);
        // How many packages would be offered (only readable with the add-on)
        if (a.entitled) quotesApi.getSettings().then((r) => setPackages(r.settings.packages.filter((p) => p.active).length)).catch(() => undefined);
      })
      .catch(() => setAccess(null));
  }, [quotesApi]);

  async function set(enabled: boolean) {
    setSaving(true);
    setError(null);
    try {
      setWidget(await api.updateWidget(widget.widgetId, { quotes: { enabled } }));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const ready = Boolean(access?.entitled && access.termsAccepted && detailed);
  return (
    <section className="card">
      <h2>Detailed quotes</h2>
      {!detailed ? (
        <p className="card-sub">
          Contact forms send enquiries only. ASHP Estimators can also offer a detailed quote after the estimate (QuoteFlow plan).
        </p>
      ) : !access?.entitled ? (
        <p className="card-sub">
          With the <Link to="/billing">QuoteFlow plan</Link>, this component can offer “Get my detailed quote” after the estimate: the exact system, fixed prices and running costs, built
          from your catalogue and packages and sent to the customer straight away.
        </p>
      ) : (
        <>
          <p className="card-sub">
            After the estimate, customers can choose <strong>Get my detailed quote</strong>. It's built from your catalogue, packages and margins and <strong>sent to them straight away</strong>,
            without you checking it first, subject to a home survey. You get a copy by email and it appears in Quotes. The estimate itself still uses your size prices above.
            {style === "small" ? " On this Compact estimator it's offered for homes with an energy certificate (the quote's answers come from it)." : ""}
          </p>
          <div className="flash flash-info">
            <AlertTriangle size={16} aria-hidden="true" />
            {/* One text element: .flash is a flex row, so bare links would become columns */}
            <span>
              Your customers see exactly the prices in your <Link to="/quotes/catalogue">catalogue</Link> and <Link to="/quotes/settings">quote settings</Link>. Check every product, package,
              cost line and margin before switching this on.
            </span>
          </div>
          {!access.termsAccepted && (
            <p className="muted">
              First accept the quote terms in <Link to="/quotes/settings">Quote settings</Link>.
            </p>
          )}
          {packages === 0 && <p className="muted">You have no packages yet, so customers would get a callback instead. Add them in Quote settings → Packages.</p>}
          {error && <div className="flash flash-danger">{error}</div>}
          <Toggle
            id="instant-quotes"
            checked={on && ready}
            label={saving ? "Saving…" : on ? "Offering detailed quotes on this component" : "Offer detailed quotes on this component"}
            onChange={(v) => (ready || !v) && void set(v)}
          />
          {on && !ready && <p className="muted">Switched on, but not offered until the quote terms are accepted.</p>}
        </>
      )}
    </section>
  );
}
